import { useState } from 'react';
import { motion } from 'motion/react';
import { GraduationCap, Music, ArrowRight } from 'lucide-react';
import { NumberTicker } from '@/components/ui/number-ticker';
import type { LucideIcon } from 'lucide-react';
import { DemoModal } from '@/components/shared/DemoModal';
import { LANDING_COLORS } from '@/constants/landing-colors';

type B2BListingType = 'school' | 'rehearsal';

interface DirectoryPromoBannerB2BProps {
  type: B2BListingType;
  onTrack?: (key: string) => void;
  variant?: 'default' | 'compact';
}

interface B2BConfig {
  icon: LucideIcon;
  overlayGradient: string;
  accentColor: string;
  headline: string;
  subheadline: string;
  stats: Array<{ value: number; prefix?: string; suffix?: string; label: string }>;
  cta: string;
  ctaColors: string;
  trackingKey: string;
  image: string;
  mirrorImage?: boolean;
}

const B2B_CONFIG: Record<B2BListingType, B2BConfig> = {
  school: {
    icon: GraduationCap,
    overlayGradient: 'from-neutral-900/85 via-neutral-800/50 to-transparent',
    accentColor: 'text-amber-400',
    headline: '¿TIENES UNA ESCUELA DE MÚSICA?',
    subheadline: 'EL SOFTWARE #1 PARA ESCUELAS EN ESPAÑA',
    stats: [
      { value: 32, prefix: '+', suffix: '%', label: 'RETENCIÓN' },
      { value: 45, prefix: '-', suffix: '%', label: 'ABSENTISMO' },
    ],
    cta: 'VER MÁS',
    ctaColors: 'bg-amber-500 text-neutral-900 hover:bg-amber-400',
    trackingKey: 'promo_escuelas',
    image:
      'https://images.unsplash.com/photo-1698620625618-c576ceadacae?w=1400&auto=format&fit=crop&q=80',
    mirrorImage: true,
  },
  rehearsal: {
    icon: Music,
    overlayGradient: 'from-neutral-900/85 via-neutral-800/50 to-transparent',
    accentColor: 'text-cyan-400',
    headline: '¿GESTIONAS UN LOCAL DE ENSAYO?',
    subheadline: 'AUTOMATIZA RESERVAS Y OLVÍDATE DEL WHATSAPP',
    stats: [
      { value: 24, suffix: '/7', label: 'RESERVAS' },
      { value: 0, suffix: '%', label: 'COMISIONES' },
    ],
    cta: 'AGENDAR DEMO',
    ctaColors: 'bg-cyan-500 text-neutral-900 hover:bg-cyan-400',
    trackingKey: 'promo_salas',
    image:
      'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?q=80&w=1170&auto=format&fit=crop',
    mirrorImage: false,
  },
};

export function DirectoryPromoBannerB2B({
  type,
  onTrack,
  variant = 'default',
}: DirectoryPromoBannerB2BProps) {
  const config = B2B_CONFIG[type];
  const Icon = config.icon;
  const [demoOpen, setDemoOpen] = useState(false);

  // Captura de lead in-situ (el registro B2B aún no es self-service: escuelas/salas
  // son restringidos). El DemoModal capta el email en Supabase y abre Calendly al enviar.
  // Cuando el registro pase a self-service, cambiar por navegación a AUTH_LINKS[...].signup.
  const handleClick = () => {
    onTrack?.(`${config.trackingKey}_click`);
    setDemoOpen(true);
  };

  const demoColors = type === 'rehearsal' ? LANDING_COLORS.salas : LANDING_COLORS.escuelas;
  const demoModal = (
    <DemoModal
      open={demoOpen}
      onOpenChange={setDemoOpen}
      colors={demoColors}
      source={type === 'rehearsal' ? 'directorio_salas' : 'directorio_escuelas'}
      companyLabel={type === 'rehearsal' ? 'Nombre de tu sala' : 'Nombre de tu escuela'}
      title={type === 'rehearsal' ? 'Solicita tu demo para salas' : 'Solicita tu demo para escuelas'}
      subtitle={type === 'rehearsal'
        ? 'Te mostramos cómo automatizar reservas y pagos de tu local de ensayo.'
        : 'Te mostramos cómo Jamsom Music ayuda a gestionar tu escuela de música.'}
    />
  );

  // Compact variant - same height as directory cards, same styling as default
  if (variant === 'compact') {
    return (
      <>
      {demoModal}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        onClick={handleClick}
        className="relative overflow-hidden rounded-2xl shadow-xl cursor-pointer group"
      >
        {/* Imagen de fondo */}
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url(${config.image})`,
            backgroundPosition: config.mirrorImage ? 'center 20%' : 'center',
            transform: config.mirrorImage ? 'scaleX(-1)' : 'none',
          }}
        />

        {/* Overlay gradiente oscuro - más oscuro en móvil */}
        <div className="absolute inset-0 bg-neutral-900/40 sm:bg-transparent" />
        <div
          className={`absolute inset-0 bg-gradient-to-r ${config.overlayGradient}`}
        />

        {/* Contenido - misma distribución que default pero altura fija */}
        <div className="relative flex h-[220px] flex-col justify-center p-5 lg:max-w-[65%] lg:p-6">
          {/* Badge pequeño */}
          <div className="mb-2 inline-flex items-center gap-2 self-start">
            <Icon className={`h-5 w-5 ${config.accentColor}`} />
            <span className="text-xs font-bold uppercase tracking-wider text-white/70">
              Para profesionales
            </span>
          </div>

          {/* Headline principal */}
          <h3 className="mb-1 text-lg font-black uppercase tracking-tight text-white lg:text-xl">
            {config.headline}
          </h3>
          <p className={`mb-3 text-xs font-bold uppercase tracking-wide ${config.accentColor} lg:text-sm`}>
            {config.subheadline}
          </p>

          {/* Stats en línea */}
          <div className="flex gap-5 mb-3">
            {config.stats.map((stat, i) => (
              <div key={i} className="flex items-baseline gap-1">
                <span className="text-xl font-black text-white lg:text-2xl">
                  {stat.prefix}
                  <NumberTicker
                    value={stat.value}
                    startValue={stat.value}
                    delay={i * 0.2}
                    className="inline"
                  />
                  {stat.suffix}
                </span>
                <span className="text-[10px] font-bold uppercase text-white/60 lg:text-xs">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>

          {/* CTA con color de acento - alineado a la izquierda */}
          <span
            className={`w-fit flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide shadow-lg transition-all group-hover:gap-2 ${config.ctaColors}`}
          >
            {config.cta}
            <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </motion.div>
      </>
    );
  }

  // Default variant - full size
  return (
    <>
    {demoModal}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-2xl shadow-xl"
    >
      {/* Imagen de fondo completa */}
      <div
        className="absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url(${config.image})`,
          backgroundPosition: config.mirrorImage ? 'center 20%' : 'center',
          transform: config.mirrorImage ? 'scaleX(-1)' : 'none',
        }}
      />

      {/* Overlay gradiente - abajo-izquierda en móvil, horizontal en desktop */}
      <div className="absolute inset-0 bg-gradient-to-tr from-neutral-900/95 via-neutral-900/60 to-transparent sm:bg-none" />
      <div
        className={`absolute inset-0 hidden sm:block bg-gradient-to-r ${config.overlayGradient}`}
      />

      {/* Contenido - estilo B2C en móvil, estilo original en desktop */}
      <div className="relative flex min-h-[398px] sm:min-h-[320px] flex-col justify-end sm:justify-center p-5 sm:p-6 lg:max-w-[55%] lg:p-8">
        {/* Badge pequeño - estilo B2C en móvil */}
        <div className="mb-2 sm:mb-3 inline-flex items-center gap-2 self-start rounded-full sm:rounded-none bg-white/10 sm:bg-transparent px-3 sm:px-0 py-1 sm:py-0 backdrop-blur-sm sm:backdrop-blur-none">
          <Icon className={`h-3.5 sm:h-5 w-3.5 sm:w-5 text-white/80 sm:${config.accentColor.replace('text-', '')}`} />
          <span className="text-xs font-bold uppercase tracking-wider text-white/80 sm:text-white/70">
            Para profesionales
          </span>
        </div>

        {/* Headline principal */}
        <h3 className="mb-1 text-lg sm:text-xl font-black uppercase tracking-tight text-white lg:text-2xl">
          {config.headline}
        </h3>
        <p className={`mb-4 sm:mb-4 text-xs sm:text-sm font-bold uppercase tracking-wide text-white/60 sm:${config.accentColor.replace('text-', '')}`}>
          {config.subheadline}
        </p>

        {/* Stats compactos en línea */}
        <div className="mb-4 sm:mb-5 flex gap-4 sm:gap-6">
          {config.stats.map((stat, i) => (
            <div key={i} className="flex items-baseline gap-1">
              <span className="text-xl sm:text-2xl font-black text-white lg:text-3xl">
                {stat.prefix}
                <NumberTicker
                  value={stat.value}
                  startValue={stat.value}
                  delay={i * 0.2}
                  className="inline"
                />
                {stat.suffix}
              </span>
              <span className="text-[10px] sm:text-xs font-bold uppercase text-white/60">
                {stat.label}
              </span>
            </div>
          ))}
        </div>

        {/* CTA - full width en móvil, fit en desktop */}
        <motion.button
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleClick}
          className={`flex w-full sm:w-fit items-center justify-center sm:justify-start gap-2 rounded-lg px-4 sm:px-5 py-2.5 text-sm font-bold uppercase tracking-wide shadow-lg transition-all hover:shadow-xl ${config.ctaColors}`}
        >
          {config.cta}
          <ArrowRight className="h-4 w-4" />
        </motion.button>
      </div>
    </motion.div>
    </>
  );
}
